import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Filter, ChevronDown, X } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { SectionHead } from "./Home";

const CATEGORY_TITLES = {
  "op-preorder-jp": "One Piece — Preorder Giapponese",
  "op-preorder-en": "One Piece — Preorder Inglese (Allocazione)",
  "singles": "Carte Singole",
  "pkm-sealed-it": "Pokémon Sealed — Italiano",
  "pkm-sealed-jp": "Pokémon Sealed — Giapponese",
  "accessori": "Accessori TCG",
  "search": "Ricerca",
};

const CATEGORY_INTROS = {
  "op-preorder-jp": "Preorder di prodotti One Piece giapponesi. Pagamento intero al preorder. Consegna in 15-20 giorni lavorativi dal momento dell'ordine (ordiniamo direttamente da fornitori giapponesi selezionati) — per i preorder con data di uscita indicata fa fede quella data.",
  "op-preorder-en": "Prodotti One Piece inglesi su ALLOCAZIONE. Al preorder versi solo un acconto: il saldo viene richiesto quando il distributore conferma le quantità assegnate. La consegna fa riferimento alla data di uscita ufficiale indicata sulla scheda prodotto.",
  "singles": "Carte singole One Piece (JP & EN) e Pokémon giapponesi. Consegna 15-20 giorni lavorativi per i prodotti giapponesi, 5-7 giorni lavorativi per le carte singole inglesi già disponibili.",
  "pkm-sealed-it": "Pokémon TCG in italiano, sigillato ufficiale. Consegna in 5-7 giorni lavorativi dal momento dell'ordine per i prodotti già disponibili.",
  "pkm-sealed-jp": "Pokémon TCG giapponese di importazione. Consegna in 15-20 giorni lavorativi dal momento dell'ordine — ordiniamo direttamente dai fornitori giapponesi. Per i preorder con data di uscita indicata fa fede quella data.",
};

const LANGS = [
  { v: "all", l: "Tutte" },
  { v: "jp", l: "🇯🇵 Giapponese" },
  { v: "en", l: "🇬🇧 Inglese" },
  { v: "it", l: "🇮🇹 Italiano" },
  { v: "other", l: "🌐 Altro" },
];

const CONDS = [
  { v: "all", l: "Tutte" },
  { v: "sealed", l: "Sealed" },
  { v: "near-mint", l: "Near Mint" },
  { v: "graded", l: "Graded" },
];

const AVAIL = [
  { v: "all", l: "Tutte" },
  { v: "in-stock", l: "Disponibile" },
  { v: "preorder", l: "Preorder" },
  { v: "sold-out", l: "Esaurito" },
];

const SORTS = [
  { v: "recent", l: "Più recenti" },
  { v: "price-asc", l: "Prezzo crescente" },
  { v: "price-desc", l: "Prezzo decrescente" },
  { v: "featured", l: "In evidenza" },
];

export default function Catalog() {
  const params = useParams();
  const [sp] = useSearchParams();
  const category = params.category || "sealed";
  const isSearch = category === "search";
  const q = sp.get("q") || "";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    language: "all",
    condition: "all",
    availability: "all",
    price_min: "",
    price_max: "",
    sort: "recent",
  });

  const apiParams = useMemo(() => {
    const p = { sort: filters.sort, limit: 200 };
    if (!isSearch) {
      p.category = category;
    } else {
      if (q) p.q = q;
    }
    if (filters.language !== "all") p.language = filters.language;
    if (filters.condition !== "all") p.condition = filters.condition;
    if (filters.availability !== "all") p.availability = filters.availability;
    if (filters.price_min) p.price_min = parseFloat(filters.price_min);
    if (filters.price_max) p.price_max = parseFloat(filters.price_max);
    return p;
  }, [category, filters, isSearch, q]);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: apiParams }).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [apiParams, category]);

  const title = isSearch ? `Ricerca: "${q}"` : CATEGORY_TITLES[category] || "Catalogo";
  const intro = isSearch ? null : CATEGORY_INTROS[category];

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 md:py-16">
      <SectionHead eyebrow={isSearch ? "Search" : "Catalog"} title={title} subtitle={`${products.length} prodotti disponibili`} />
      {intro && (
        <div className="mt-4 border-l-2 border-[#C9A227] bg-[#C9A227]/5 px-4 py-3 max-w-3xl">
          <p className="text-sm text-[#D1D5DB] leading-relaxed">{intro}</p>
        </div>
      )}

      <div className="mt-8 flex flex-col lg:flex-row gap-6">
        {/* Filters sidebar */}
        <aside className={`lg:w-64 lg:block ${showFilters ? "block fixed inset-0 z-40 bg-[#090909] p-4 overflow-y-auto lg:relative lg:p-0 lg:inset-auto lg:bg-transparent" : "hidden"}`}>
          <div className="lg:sticky lg:top-24 space-y-5">
            <div className="flex justify-between items-center lg:hidden">
              <h4 className="font-display text-lg text-white">Filtri</h4>
              <button data-testid="close-filters" onClick={() => setShowFilters(false)} className="p-2 text-white"><X size={18} /></button>
            </div>
            <FilterGroup label="Lingua">
              {LANGS.map((o) => (
                <RadioLine key={o.v} name="language" value={o.v} label={o.l} filters={filters} setFilters={setFilters} />
              ))}
            </FilterGroup>
            <FilterGroup label="Condizione">
              {CONDS.map((o) => (
                <RadioLine key={o.v} name="condition" value={o.v} label={o.l} filters={filters} setFilters={setFilters} />
              ))}
            </FilterGroup>
            <FilterGroup label="Disponibilità">
              {AVAIL.map((o) => (
                <RadioLine key={o.v} name="availability" value={o.v} label={o.l} filters={filters} setFilters={setFilters} />
              ))}
            </FilterGroup>
            <FilterGroup label="Prezzo">
              <div className="flex gap-2">
                <input data-testid="filter-price-min" type="number" placeholder="Min €" value={filters.price_min}
                  onChange={(e) => setFilters({ ...filters, price_min: e.target.value })}
                  className="w-full bg-black border border-[#C9A227]/25 rounded-sm px-3 py-2 text-sm text-white outline-none focus:border-[#C9A227]"/>
                <input data-testid="filter-price-max" type="number" placeholder="Max €" value={filters.price_max}
                  onChange={(e) => setFilters({ ...filters, price_max: e.target.value })}
                  className="w-full bg-black border border-[#C9A227]/25 rounded-sm px-3 py-2 text-sm text-white outline-none focus:border-[#C9A227]"/>
              </div>
            </FilterGroup>
          </div>
        </aside>

        <div className="flex-1">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
            <button data-testid="open-filters" onClick={() => setShowFilters(true)} className="lg:hidden btn-ghost-gold text-xs">
              <Filter size={14} /> Filtri
            </button>
            <div className="ml-auto flex items-center gap-2">
              <label className="text-xs font-mono-ps uppercase tracking-widest text-[#9CA3AF]">Ordina</label>
              <div className="relative">
                <select
                  data-testid="sort-select"
                  value={filters.sort}
                  onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                  className="bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2 pr-8 text-sm text-white outline-none appearance-none"
                >
                  {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
                <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#C9A227]"/>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-80 bg-[#111] border border-[#C9A227]/10 animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="border border-[#C9A227]/20 rounded-sm p-10 text-center text-[#9CA3AF]">
              Nessun prodotto trovato con questi filtri.
            </div>
          ) : (
            <div data-testid="catalog-grid" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FilterGroup = ({ label, children }) => (
  <div className="border border-[#C9A227]/15 rounded-sm p-4 bg-[#0b0b0d]">
    <div className="eyebrow mb-3">{label}</div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const RadioLine = ({ name, value, label, filters, setFilters }) => (
  <label className="flex items-center gap-2 cursor-pointer text-sm text-white/85 hover:text-[#C9A227]">
    <input
      type="radio"
      name={name}
      checked={filters[name] === value}
      onChange={() => setFilters({ ...filters, [name]: value })}
      data-testid={`filter-${name}-${value}`}
      className="accent-[#C9A227]"
    />
    <span>{label}</span>
  </label>
);
